import React, { useState } from 'react';
import Icon from '@/components/ui/Icon';
import * as Routes from '@/modules/routes';
import cn from '@/utils/cn';
import { railItemClass, sectionLabelClass } from '../railClasses';
import { RailPage, globalPages, toolPages } from './contentPages';
import { PageLinks, useRailLink } from './RailLink';
import UnitGroup, { LessonItem } from './UnitGroup';
import useProgramTree from './useProgramTree';

// AIDEV-NOTE: One program tree for both shells: the SPA's program drill (ProgramNavRail, inside
// SPANavSlot) and the legacy content rail (admin/LegacyContentRail, inside SPARail). Structure
// only — a Table of Contents row, then units → lessons → sections, under the shell's program
// switcher. A row's chevron opens it; its title goes to the node's home page (Edit Unit, Sections,
// Text). The pages of a node are the
// context bar's job (admin/SysadminContextBar), not the tree's. Whatever unitId/lessonId/sectionId
// say the user is inside starts open. Links render through RailLinkProvider: the SPA's ContentView
// provides router links, the legacy rail gets the plain-anchor default.

const SKELETON_ROW_WIDTHS = ['tw-w-44', 'tw-w-36', 'tw-w-48', 'tw-w-40'];

const TreeSkeleton = () => (
  <div aria-hidden="true" className="tw-px-2 tw-pt-2">
    {SKELETON_ROW_WIDTHS.map(width => (
      <div
        key={width}
        className={`tw-my-2 tw-h-3.5 tw-animate-pulse tw-rounded tw-bg-spa-bg-skeleton-subtle ${width}`}
      />
    ))}
  </div>
);

interface CollapsiblePageSectionProps {
  label: string;
  pages: RailPage[];
}

// Global and Tools aren't part of the program's structure, so they sit below the tree and start closed.
export const CollapsiblePageSection = ({ label, pages }: CollapsiblePageSectionProps) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="tw-px-2">
      <button
        aria-expanded={open}
        className={cn(
          sectionLabelClass,
          'tw-flex tw-min-h-[24px] tw-w-full tw-cursor-pointer tw-items-center tw-gap-1 tw-border-0 tw-bg-transparent',
          'tw-text-left hover:tw-text-spa-on-bg',
        )}
        type="button"
        onClick={() => setOpen(!open)}
      >
        {label}
        <Icon className={cn('tw-transition-transform', open && 'tw-rotate-90')} icon="ChevronRight" size={10} />
      </button>
      {open && (
        <nav aria-label={label}>
          <PageLinks className={railItemClass} pages={pages} />
        </nav>
      )}
    </div>
  );
};

const toggleIn = (setter: React.Dispatch<React.SetStateAction<Set<number>>>) => (id: number) => {
  setter((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });
};

const GHOST_UNIT_ID = 0;

export interface ProgramTreeProps {
  lessonId?: number;
  programId: number;
  sectionId?: number;
  showSearch?: boolean;
  unitId?: number;
}

const ProgramTree = ({
  lessonId,
  programId,
  sectionId,
  showSearch = false,
  unitId,
}: ProgramTreeProps) => {
  const { allLessons, ghostLessons, hasUnits, isLoading, units } = useProgramTree(programId);
  const [expandedUnits, setExpandedUnits] = useState<Set<number>>(() => new Set(unitId ? [unitId] : []));
  const [expandedLessons, setExpandedLessons] = useState<Set<number>>(() => new Set(lessonId ? [lessonId] : []));
  const toggleUnit = toggleIn(setExpandedUnits);
  const toggleLesson = toggleIn(setExpandedLessons);
  const TocLink = useRailLink();

  return (
    <>
      <nav aria-label="Program tree" className="tw-px-2 tw-pb-2 tw-pt-1">
        <TocLink
          className={railItemClass}
          page={{
            href: Routes.admin_program_lessons_path(programId),
            label: 'Table of Contents',
            to: `/content/programs/${programId}`,
          }}
        >
          Table of Contents
        </TocLink>
        <div>
          {isLoading && <TreeSkeleton />}
          {!isLoading && hasUnits && units.map(unit => (
            <UnitGroup
              key={unit.id}
              current={unit.id === unitId}
              expanded={expandedUnits.has(unit.id)}
              expandedLessons={expandedLessons}
              lessonId={lessonId}
              programId={programId}
              sectionId={sectionId}
              unit={unit}
              onToggle={() => toggleUnit(unit.id)}
              onToggleLesson={toggleLesson}
            />
          ))}
          {!isLoading && hasUnits && ghostLessons.length > 0 && (
            <UnitGroup
              current={false}
              expanded={expandedUnits.has(GHOST_UNIT_ID)}
              expandedLessons={expandedLessons}
              lessonId={lessonId}
              programId={programId}
              sectionId={sectionId}
              unit={{ display_number: '', id: GHOST_UNIT_ID, lessons: ghostLessons, title: 'Unitless Lessons' }}
              onToggle={() => toggleUnit(GHOST_UNIT_ID)}
              onToggleLesson={toggleLesson}
            />
          )}
          {!isLoading && !hasUnits && allLessons.map(lesson => (
            <LessonItem
              key={lesson.id}
              current={lesson.id === lessonId}
              expanded={expandedLessons.has(lesson.id)}
              lesson={lesson}
              programId={programId}
              sectionId={sectionId}
              onToggle={() => toggleLesson(lesson.id)}
            />
          ))}
        </div>
      </nav>
      <CollapsiblePageSection label="Global" pages={globalPages(programId)} />
      <CollapsiblePageSection label="Tools" pages={toolPages(programId, showSearch)} />
    </>
  );
};

export default ProgramTree;
